/* =====================================================================
   Pen Tech – motion layer
   - scroll reveals for pt-* sections (html.pt-motion, set in theme.liquid)
   - rolling bar: constant px/s speed, pauses off-screen, pause/play button (v26)
   - header shadow after scroll, cart bubble bump
   - cart link: aria-label "Cart, N items" kept in step with the count (v26)
   Everything is visible without this file: the head script removes
   html.pt-motion if this file never confirms with html.pt-motion-ok.
   ===================================================================== */
(function () {
  'use strict';
  var root = document.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* ---------- Reveal on scroll ---------- */
  // [selector, stagger children?]
  var GROUPS = [
    ['.pt-heading-row', false],
    ['.pt-cats > .pt-cat', true],
    ['.pt-trust > .pt-trust__item', true],
    ['.pt-promo', false],
    ['.pt-brands-carousel', false],
    ['.pt-cta__box', false],
    ['.pt-steps__list > .pt-steps__item', true],
    ['.pt-checklist__box', false],
    ['.pt-story__grid > *', true],
    ['.pt-specs__grid > .pt-spec', true],
    ['.pt-contact__cards > *', true],
    ['.pt-contact__form-box', false],
    ['.pt-acc__layout', false],
    ['.pt-info__item', false],
    ['.pt-tif__layout > *', true],
    ['.pt-footer__grid > div', true]
  ];
  var MAX_STAGGER = 8;

  function initReveal() {
    if (!root.classList.contains('pt-motion') || !hasIO) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        io.unobserve(el);
        el.classList.add('is-in');
        // hand the element back to its own hover transitions once it has settled
        var delay = (parseInt(el.style.getPropertyValue('--pt-i'), 10) || 0) * 70;
        setTimeout(function () {
          el.classList.remove('pt-rv', 'is-in');
          el.style.removeProperty('--pt-i');
        }, 900 + delay);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    GROUPS.forEach(function (g) {
      var nodes = document.querySelectorAll(g[0]);
      var counters = new Map();
      nodes.forEach(function (el) {
        if (el.dataset.ptRv) return;
        el.dataset.ptRv = '1';
        // only animate what is still below the fold: content that is already
        // on screen (or scrolled past) never blinks out and back in
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.95) return;
        el.classList.add('pt-rv');
        if (g[1]) {
          var p = el.parentNode;
          var n = counters.get(p) || 0;
          counters.set(p, n + 1);
          el.style.setProperty('--pt-i', Math.min(n, MAX_STAGGER));
        }
        io.observe(el);
      });
    });
  }

  /* ---------- Rolling bar ---------- */
  function initMarquee() {
    var bars = document.querySelectorAll('[data-pt-mq]');
    if (!bars.length) return;
    function size(bar) {
      var speed = parseFloat(bar.dataset.speed) || 45;
      bar.querySelectorAll('[data-pt-mq-row]').forEach(function (row, i) {
        var list = row.querySelector('.pt-mq__list');
        if (!list) return;
        var w = list.getBoundingClientRect().width;
        // the bottom row (large words) travels a touch slower so the two rows drift apart
        var s = i === 0 ? speed : speed * 0.8;
        if (w > 0) row.style.setProperty('--pt-mq-dur', (w / s).toFixed(2) + 's');
      });
    }
    bars.forEach(size);
    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () { bars.forEach(size); }, 150);
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { bars.forEach(size); });
    if (hasIO) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { e.target.classList.toggle('is-paused', !e.isIntersecting); });
      });
      bars.forEach(function (b) { io.observe(b); });
    }
    // pause / play: a class of its own, so the off-screen observer never resumes a strip the visitor stopped
    bars.forEach(function (bar) {
      var btn = bar.querySelector('[data-pt-mq-toggle]');
      if (!btn || btn.dataset.ptReady) return;
      btn.dataset.ptReady = '1';
      btn.addEventListener('click', function () {
        var paused = btn.getAttribute('data-paused') !== 'true';
        btn.setAttribute('data-paused', paused ? 'true' : 'false');
        btn.setAttribute('aria-label', paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
        bar.classList.toggle('is-user-paused', paused);
      });
    });
  }

  /* ---------- Header shadow once the page scrolls ---------- */
  function initHeader() {
    var ticking = false;
    function update() {
      root.classList.toggle('pt-scrolled', window.scrollY > 8);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ---------- Cart bubble bump when the count changes ---------- */
  function initCartBump() {
    if (reduce || !('MutationObserver' in window)) return;
    document.querySelectorAll('.m-cart-count-bubble').forEach(function (b) {
      var last = b.textContent.trim();
      new MutationObserver(function () {
        var now = b.textContent.trim();
        if (now === last) return;
        last = now;
        b.classList.remove('pt-bump');
        void b.offsetWidth;
        b.classList.add('pt-bump');
      }).observe(b, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ---------- Cart link name: "Cart, 2 items" / «السلة، فيها منتجين» ---------- */
  // snippets/cart.liquid prints the label for every plural form (data-pt-cart-labels); the count
  // itself is updated by Minimog's <m-cart-count>, so the label follows it here.
  function initCartLabel() {
    var links = document.querySelectorAll('.m-cart-icon-bubble[data-pt-cart-labels]');
    if (!links.length) return;
    var rules = null;
    try { rules = new Intl.PluralRules(root.lang || 'en'); } catch (e) { rules = null; }
    var REP = { zero: '0', one: '1', two: '2', few: '3', many: '11', other: '100' };
    function label(map, n) {
      var cat = 'other';
      if (rules) { try { cat = rules.select(n); } catch (e) { cat = 'other'; } }
      var tpl = map[cat] || map.other || '';
      return tpl.split(REP[cat] || REP.other).join(String(n));
    }
    links.forEach(function (a) {
      if (a.dataset.ptCartReady) return;
      var map = null;
      try { map = JSON.parse(a.getAttribute('data-pt-cart-labels')); } catch (e) { map = null; }
      var count = a.querySelector('.m-cart-count');
      if (!map || !count) return;
      a.dataset.ptCartReady = '1';
      function sync() {
        var n = parseInt(count.textContent, 10);
        if (isNaN(n)) n = 0;
        var text = label(map, n); // "99+" reads as 99
        if (text && a.getAttribute('aria-label') !== text) a.setAttribute('aria-label', text);
      }
      if ('MutationObserver' in window) {
        new MutationObserver(sync).observe(count, { childList: true, characterData: true, subtree: true });
      }
    });
  }

  function init() {
    initReveal();
    initMarquee();
    initHeader();
    initCartBump();
    initCartLabel();
    root.classList.add('pt-motion-ok');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // sections re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function () { initMarquee(); });
})();
